import { Component, TSX, h } from "../TSX/TSX";
import { BasicComponent } from "./BasicComponent";
import { BasicEvent, UniqueArray } from "@h4x/common";

const $remove = Symbol.for("$TSX_Fragment_Remove");
const $rendered = Symbol.for("$TSX_Fragment_Rendered");
const $attach = Symbol.for("$TSX_Fragment_Attach");

export abstract class Fragment<T> extends BasicComponent<T & { fragmentList: Fragment.AbstractList<Fragment<T>> }> {
	public [$rendered]: TSX.RenderedComponent<this>;

	get rendered() {
		return this[$rendered];
	}

	public remove() {
		this.props.fragmentList[$remove](this[$rendered]);
		this[$rendered].destroy();
	}

	/*public moveTo(pos: number) {
		this.props.fragmentList.moveFragment(this[Rendered], pos);
	}

	public moveUp() {
		let index = this.index();
		this.props.fragmentList.moveFragment(this[Rendered], index - 1);
	}

	public moveDown() {
		let index = this.index();
		// +2 as it need to move over itself and the next element
		this.props.fragmentList.moveFragment(this[Rendered], index + 2);
	}

	public index() {
		return this.props.fragmentList.indexOf(this[Rendered]);
	}*/

	/** should only include modified props */
	public updateProps(props: Partial<T>) {
		this[$rendered].vnode.internalUpdateProps({ ...this.props, ...props });
	}

	public updateProp(prop: keyof T, value: T[typeof prop]) {
		this[$rendered].vnode.internalUpdateProps({ ...this.props, [prop]: value });
	}

	static getInternal<T extends Fragment<any>>(fragment: T) {
		return fragment[$rendered];
	}
}

export namespace Fragment {
	export const $fragments = Symbol.for("$TSX_Fragments");

	export abstract class AbstractList<T extends Fragment<any>> {
		protected component: Container<T>;

		public abstract [Symbol.iterator](): Iterator<TSX.RenderedComponent<T>>;

		public *instances() {
			for (const fragment of this) {
				yield fragment.vnode.instance;
			}
		}

		public abstract get first(): TSX.RenderedComponent<T> | undefined;
		public abstract get firstInstance(): T | undefined;
		public abstract get last(): TSX.RenderedComponent<T> | undefined;
		public abstract get lastInstance(): T | undefined;

		public abstract get length(): number;
		abstract indexOf(rendered: TSX.RenderedComponent<T> | T): number;

		protected createFragment<K extends T>(FragmentClass: new () => K, args: Omit<TSX.Props<K>, "fragmentList">) {
			let fragment = <FragmentClass {...args} fragmentList={this} />;
			let rendered = TSX.prerenderComponent<T>(fragment);
			let instance = rendered.vnode.instance as K;
			instance[$rendered] = rendered as TSX.RenderedComponent<K>;
			return { rendered, instance };
		}

		[$attach](component: Container<T>) {
			if (this.component !== undefined) { throw new Error("FragmentList already attached to a component"); }
			this.component = component;

			for (const fragment of this) {
				this.component.attachFragment(fragment);
			}
		}
		abstract [$remove](rendered: TSX.RenderedComponent<T>): boolean;
	}
	export class List<T extends Fragment<any>> extends AbstractList<T> {
		private readonly fragments = new UniqueArray<TSX.RenderedComponent<T>>();

		public get length() { return this.fragments.length; }
		public get first() { return this.fragments[0]; }
		public get firstInstance() { return this.fragments[0]?.vnode.instance; }
		public get last() { return this.fragments[this.fragments.length - 1]; }
		public get lastInstance() { return this.fragments[this.fragments.length - 1]?.vnode.instance; }

		public *[Symbol.iterator]() {
			for (const fragment of this.fragments) {
				yield fragment;
			}
		}

		public get(at: number) {
			return this.fragments[at];
		}

		public getInstance(at: number) {
			return this.fragments[at].vnode.instance;
		}

		public indexOf(rendered: TSX.RenderedComponent<T> | T) {
			if (rendered instanceof TSX.Rendered) {
				return this.fragments.indexOf(rendered);
			} else {
				return this.fragments.indexOf(rendered[$rendered] as TSX.RenderedComponent<T>);
			}
		}

		/** Add fragment before `at` or at the end */
		public addFragment<K extends T>(FragmentClass: new () => K, args: Omit<TSX.Props<K>, "fragmentList">, at?: number | T) {
			let { rendered, instance } = this.createFragment<K>(FragmentClass, args);

			if (typeof (at) === "object") {
				let index = this.indexOf(at[$rendered] as any);
				if (index >= 0) {
					at = index;
				} else {
					console.warn("Couldn't find 'at' fragment when trying to add fragment!");
					at = undefined;
				}
			}

			if (at === 0) {
				this.fragments.unshift(rendered);
			} else if (at !== undefined) {
				this.fragments.insertAt(at, rendered);
			} else {
				this.fragments.push(rendered);
			}

			if (this.component !== undefined) {
				this.component.attachFragment(rendered, at);
			}
			return instance;
		}

		public moveFragment(rendered: TSX.RenderedComponent<T>, to?: number) {
			// make sure the fragment is in the list
			if (this.fragments.indexOf(rendered) === -1) {
				throw new Error("Can't move fragment to a different list using moveFragment (use moveTo instead)");
			}

			if (to !== undefined) {
				if (to <= 0) {
					this.fragments.unshift(rendered);
				} else {
					this.fragments.insertAt(to, rendered);
				}
			} else {
				this.fragments.push(rendered);
			}

			if (this.component !== undefined) {
				this.component.attachFragment(rendered, to);
			}
		}

		/*
			FragmentList needs to have a way to move Fragments around (and to sort them?)
			swap? swap range? move range?
		*/
		public detachFragment(rendered: TSX.RenderedComponent<T>) {
			if (this.fragments.removeElement(rendered)) {
				rendered.detach();
				return true;
			}
			return false;
		}

		public moveTo(rendered: TSX.RenderedComponent<T>, list: List<T>, at?: number) {
			this.detachFragment(rendered);

			if (at === 0) {
				list.fragments.unshift(rendered);
			} else if (at !== undefined) {
				list.fragments.insertAt(at, rendered);
			} else {
				list.fragments.push(rendered);
			}

			if (list.component !== undefined) {
				list.component.attachFragment(rendered, at);
			}

			(rendered.vnode.instance.props.fragmentList as any) = list;
		}

		public detachAll() {
			for (const fragment of this.fragments) {
				fragment.detach();
			}
			this.fragments.clear();
		}

		public destroyAll() {
			for (const fragment of this.fragments) {
				fragment.destroy();
			}
			this.fragments.clear();
		}

		[$attach](component: Container<T>) {
			super[$attach](component);
		}

		/** internal */
		public [$remove](rendered: TSX.RenderedComponent<T>) {
			if (this.fragments.removeElement(rendered)) {
				return true;
			}
			return false;
		}
	}


	export class Single<T extends Fragment<any>> extends AbstractList<T> {
		private fragment?: TSX.RenderedComponent<T>;

		public get length() { return this.fragment ? 1 : 0; }
		public get first() { return this.fragment; }
		public get firstInstance() { return this.fragment?.vnode.instance; }
		public get last() { return this.fragment; }
		public get lastInstance() { return this.fragment?.vnode.instance; }

		public *[Symbol.iterator]() {
			if (this.fragment) {
				yield this.fragment;
			}
		}

		public indexOf(rendered: TSX.RenderedComponent<T> | T) {
			if (rendered instanceof TSX.Rendered) {
				return this.fragment === rendered ? 0 : -1;
			} else {
				return this.fragment === rendered[$rendered] as TSX.RenderedComponent<T> ? 0 : -1;
			}
		}

		public set<K extends T>(FragmentClass: new () => K, args: Omit<TSX.Props<K>, "fragmentList">) {
			if (this.fragment !== undefined) {
				this.fragment.destroy();
			}
			let { rendered, instance } = this.createFragment<K>(FragmentClass, args);

			this.fragment = rendered;

			if (this.component !== undefined) {
				this.component.attachFragment(rendered);
			}
			return instance;
		}

		public attach(rendered: TSX.RenderedComponent<T>) {
			if (this.fragment === rendered) {
				return;
			} else if (this.fragment !== undefined) {
				this.fragment.destroy();
			}
			this.fragment = rendered;
			if (this.component !== undefined) {
				this.component.attachFragment(rendered);
			}
		}

		public get() {
			return this.fragment;
		}

		public get instance() {
			return this.fragment?.vnode.instance;
		}

		public detach() {
			if (this.fragment) {
				this.fragment.detach();
				this.fragment = undefined;
			}
		}

		public destroy() {
			if (this.fragment) {
				this.fragment.destroy();
				this.fragment = undefined;
			}
		}

		/** internal */
		public [$remove](rendered: TSX.RenderedComponent<T>) {
			if (this.fragment === rendered) {
				this.fragment = undefined;
				return true;
			}
			return false;
		}
	}

	export namespace Container {
		export interface Props<T extends Fragment<any>> extends JSX.HTMLAttributes<HTMLDivElement> {
			nodeType?: string;
			fragmentList: AbstractList<T>;
			key: string;
			[Component.Children]?: undefined;
		}
	}

	export class Container<T extends Fragment<any>, K = {}> extends BasicComponent<K & Container.Props<T>>{
		#element: HTMLDivElement;

		get element() {
			return this.#element;
		}

		get [Fragment.$fragments]() {
			return this.props.fragmentList as AbstractList<T>;
		}

		public readonly onElement = new BasicEvent<(element: HTMLDivElement) => void>();

		@BasicComponent.bind()
		private attachContainer(instance: HTMLDivElement) {
			if (this.#element !== undefined) {
				if (this.#element !== instance) {
					throw new Error("Trying to reassign fragment container!");
				}
			} else {
				this.#element = instance;
				this.props.fragmentList[$attach](this);
				this.onElement.execute(instance);
			}
		}

		attachFragment(rendered: TSX.RenderedComponent<T>, at?: number | Node) {
			rendered.attach(this.#element, at);
		}

		public render() {
			const { fragmentList, nodeType, ...props } = this.props;
			let Type = (nodeType as string) ?? "raw";
			return <Type {...props} ref={this.attachContainer} raw />;
		}
	}
}
